/**
 * EmailTemplatesPage — list + edit-modal + preview-iframe
 *
 * Endpoints:
 *  GET    /api/admin/email-templates                     → { items, total }
 *  PATCH  /api/admin/email-templates/{id}                → updated EmailTemplateResponse
 *  POST   /api/admin/email-templates/{id}/preview        → { onderwerp_rendered, body_html_rendered, ... }
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPatch, adminPost, formatApiError } from "../../lib/adminApi";
import { PageHeader, ErrorBanner, OvergeslagenBanner, LoadingRow, EmptyRow, formatDate } from "../../components/admin/AdminUI";
import { BERICHT_NAMEN, noemNetjes } from "../../lib/namen";

function PlaceholderChips({ placeholders }) {
  if (!Array.isArray(placeholders) || placeholders.length === 0) {
    return <span className="text-xs text-slate-500">geen</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {placeholders.map((p) => (
        <code key={p} data-testid={`placeholder-chip-${p}`}
          className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-700">{`{{${p}}}`}</code>
      ))}
    </div>
  );
}

function PreviewModal({ template, onClose }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries((template.placeholders || []).map((p) => [p, `«${p}»`])));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const previewMut = useMutation({
    mutationFn: () => adminPost(`/api/admin/email-templates/${template.template_id}/preview`, { placeholder_values: values }),
    onSuccess: (data) => { setResult(data); setError(null); },
    onError: (e) => setError(e),
  });

  return (
    <div data-testid="template-preview-modal" role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      /* Doorzichtigheid kan niet via een kleurklasse: die verwijst nu naar een
         token, en daar kan Tailwind geen alfa op leggen. */
      style={{ background: "rgba(11, 32, 39, 0.5)" }}>
      <div className="w-full max-w-4xl rounded-lg bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">Voorbeeld — {noemNetjes(template.type, BERICHT_NAMEN)}</h3>
          <button data-testid="btn-preview-close" onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">&times;</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Placeholder-waarden</div>
            {Object.keys(values).length === 0 ? (
              <p className="text-xs text-slate-500">Geen placeholders gedefinieerd.</p>
            ) : Object.entries(values).map(([k, v]) => (
              <label key={k} className="block mb-2">
                <span className="text-xs text-slate-700">{`{{${k}}}`}</span>
                <input data-testid={`preview-input-${k}`} value={v}
                  onChange={(e) => setValues((s) => ({ ...s, [k]: e.target.value }))}
                  className="mt-0.5 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" />
              </label>
            ))}
            <button type="button" data-testid="btn-preview-render"
              onClick={() => previewMut.mutate()} disabled={previewMut.isPending}
              className="mt-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 min-h-[44px]">
              {previewMut.isPending ? "Rendering…" : "Render preview"}
            </button>
            <ErrorBanner error={error} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Resultaat</div>
            {result ? (
              <div data-testid="template-preview-result" className="space-y-2 text-sm">
                <div><span className="font-semibold">Onderwerp: </span>{result.onderwerp_rendered}</div>
                <iframe title="HTML preview" data-testid="template-preview-iframe"
                  srcDoc={result.body_html_rendered} sandbox=""
                  className="w-full h-64 rounded border border-slate-200 bg-white" />
                <details className="text-xs text-slate-600">
                  <summary>Plain-text-versie</summary>
                  <pre className="mt-1 whitespace-pre-wrap font-mono bg-slate-50 p-2 rounded">{result.body_text_rendered}</pre>
                </details>
                {Array.isArray(result.missing_placeholders) && result.missing_placeholders.length > 0 ? (
                  <p data-testid="preview-missing-placeholders" className="text-xs text-amber-800">
                    Ontbrekend in waardes: {result.missing_placeholders.join(", ")}
                  </p>
                ) : null}
              </div>
            ) : <p className="text-xs text-slate-500">Klik "Render preview" om het resultaat te tonen.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditModal({ template, onClose, onSaved }) {
  const [form, setForm] = useState({
    onderwerp: template.onderwerp || "",
    body_html: template.body_html || "",
    body_text: template.body_text || "",
    actief: template.actief ?? true,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(null);

  const saveMut = useMutation({
    mutationFn: () => adminPatch(`/api/admin/email-templates/${template.template_id}`, form),
    onSuccess: (data) => { toast.success("Template opgeslagen"); onSaved?.(data); onClose(); },
    onError: (e) => setError(e),
  });

  const subjectInvalid = !form.onderwerp.trim();

  return (
    <>
      <div data-testid="template-edit-modal" role="dialog" aria-modal="true"
        className="fixed inset-0 z-40 flex items-center justify-center p-4"
        /* Doorzichtigheid kan niet via een kleurklasse: die verwijst nu naar een
           token, en daar kan Tailwind geen alfa op leggen. */
        style={{ background: "rgba(11, 32, 39, 0.5)" }}>
        <div className="w-full max-w-3xl rounded-lg bg-white p-5 shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Template bewerken</h3>
              <p className="text-xs text-slate-500">{noemNetjes(template.type, BERICHT_NAMEN)} · v{template.versie || "1"} · <span className="font-mono">{template.type}</span></p>
            </div>
            <button data-testid="btn-edit-close" onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">&times;</button>
          </div>
          <ErrorBanner error={error} />
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase text-slate-500">Onderwerp</span>
              <input data-testid="edit-onderwerp" value={form.onderwerp}
                onChange={(e) => setForm((s) => ({ ...s, onderwerp: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 min-h-[44px]" />
              {subjectInvalid ? <span data-testid="edit-onderwerp-error" className="text-xs text-rose-700">Onderwerp is verplicht</span> : null}
            </label>
            <div>
              <span className="text-xs font-semibold uppercase text-slate-500">Placeholders</span>
              <div className="mt-1"><PlaceholderChips placeholders={template.placeholders} /></div>
            </div>
            <label className="block">
              <span className="text-xs font-semibold uppercase text-slate-500">Body (HTML)</span>
              <textarea data-testid="edit-body-html" value={form.body_html}
                onChange={(e) => setForm((s) => ({ ...s, body_html: e.target.value }))}
                rows={10} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase text-slate-500">Body (plain-text fallback)</span>
              <textarea data-testid="edit-body-text" value={form.body_text}
                onChange={(e) => setForm((s) => ({ ...s, body_text: e.target.value }))}
                rows={5} className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs" />
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" data-testid="edit-actief" checked={form.actief}
                onChange={(e) => setForm((s) => ({ ...s, actief: e.target.checked }))} />
              <span className="text-sm text-slate-700">Template actief (anders wordt niet verzonden)</span>
            </label>
          </div>
          <div className="mt-5 flex justify-between gap-2">
            <button type="button" data-testid="btn-edit-preview"
              onClick={() => setShowPreview(true)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm min-h-[44px]">Preview</button>
            <div className="flex gap-2">
              <button type="button" data-testid="btn-edit-cancel" onClick={onClose}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm min-h-[44px]">Annuleren</button>
              <button type="button" data-testid="btn-edit-save"
                onClick={() => saveMut.mutate()} disabled={subjectInvalid || saveMut.isPending}
                className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 min-h-[44px]">
                {saveMut.isPending ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showPreview ? <PreviewModal template={{ ...template, ...form }} onClose={() => setShowPreview(false)} /> : null}
    </>
  );
}

export default function EmailTemplatesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const q = useQuery({ queryKey: ["email-templates-list"], queryFn: () => adminGet("/api/admin/email-templates", { limit: 50 }) });
  const items = q.data?.items || [];

  return (
    <div data-testid="email-templates-page">
      <PageHeader title="Email-templates" subtitle="Bewerk onderwerp, HTML/text body en preview met testwaarden" />
      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="sjabloon" meervoud="sjablonen"
        testid="email-templates-overgeslagen"
      />
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-700">
            <tr>
              <th className="px-4 py-2 text-left">Soort bericht</th>
              <th className="px-4 py-2 text-left">Onderwerp</th>
              <th className="px-4 py-2 text-left">Placeholders</th>
              <th className="px-4 py-2 text-left">Actief</th>
              <th className="px-4 py-2 text-left">Aangepast</th>
              <th className="px-4 py-2 text-right">Actie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.isLoading ? <LoadingRow cols={6} /> :
             items.length === 0 ? <EmptyRow cols={6} /> :
             items.map((t) => (
              <tr key={t.template_id} data-testid={`template-row-${t.type}`}>
                <td className="px-4 py-2 text-sm" title={t.type || ""}>{noemNetjes(t.type, BERICHT_NAMEN)}</td>
                <td className="px-4 py-2 text-sm">{t.onderwerp}</td>
                <td className="px-4 py-2"><PlaceholderChips placeholders={t.placeholders} /></td>
                <td className="px-4 py-2 text-xs">{t.actief ? <span className="text-emerald-800">Ja</span> : <span className="text-slate-500">Nee</span>}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{formatDate(t.updated_at)}</td>
                <td className="px-4 py-2 text-right">
                  <button type="button" data-testid={`btn-edit-${t.type}`} onClick={() => setEditing(t)}
                    className="rounded-md border border-slate-300 px-2.5 py-1.5 text-xs hover:bg-slate-50 min-h-[36px]">Bewerken</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing ? <EditModal template={editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["email-templates-list"] })} /> : null}
    </div>
  );
}
